import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportMetaInformationDTO,
    AnnualReportProvider,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully
} from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreLayoutProvider,
    DateLocalePipe,
    FIRST_ELEMENT_INDEX,
    IAumicoStepControl,
    INotificationData,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    OkCancelDialogActions,
} from '@modeso/aumico-lib-core-fe';
import {
    IAnnualReportAccordionsData,
    IAnnualReportDefaultSettings,
    IFLIForNotes,
    IFxNodeStamp,
    IFxNotesContext,
    INotesDelegate,
    INotesTemplateByLegalFormAndAccountBasisRequest,
} from '@modeso/aumico-lib-notes-fe';
import { FCPState, GetOrganizationDto, IUserPreferences, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import {
    AnnualReportStep,
    AutofillOptions,
    IAccountMappingTemplate,
    IAnnualReport,
    IFinStatements,
    INotes,
    ReportType
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import isEqual from 'lodash.isequal'; // Or your own deep compare logic
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, find, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
    onGetAnnualReportToCopyNotesStructureFromFailed,
    onGetAnnualReportToCopyNotesStructureFromSuccessfully,
} from '../../../../../../aumico-lib-annualreport-fe/src/lib/actions/annualReport.actions';
import { NOTES } from '../../../../../../aumico-lib-annualreport-fe/src/lib/constants/autofill-options';
import { ProjectToCopyFrom } from '../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/project-to-copy-from.interface';
import { NotesHelper } from '../../../../../../aumico-lib-notes-fe/src/lib/helpers/notes.helper';
import { NotesLiveDataHelper } from '../helpers/notes-live-data.helper';
import { NotesUnsavedChangesHelper } from '../helpers/notes-unsaved-changes.helper';
import { NotesVersionHelper } from '../helpers/notes-version.helper';
import { ProjectsOverviewActions, ProjectsOverviewState } from '../../../../../../aumico-lib-projects-fe/src/lib';
import { selectActiveProjectId } from '../../../../../../aumico-lib-projects-fe/src/lib/reducers/projects-overview.reducer';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { DataState } from '../../../shared/enum/datastate.enum';
import { templateConstants } from '../../../shared/template-constants';
import { AppStateDispatcher } from '../../../state/app.state.dispatcher';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { FlisMapper } from './../mappers/flis.mapper';

const debug = Debug('aumico:project-reports-fe:annualReportNotesPage-NotesPage');

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-notes-attachments',
    templateUrl: 'notes.page.html',
    styleUrls: ['notes.page.scss'],
})
export class NotesPage extends AnnualReportSubPage implements OnInit, INotesDelegate, IAumicoStepControl {
    isNotesSavedBefore = false;
    combination: INotesTemplateByLegalFormAndAccountBasisRequest | undefined;
    arTemplateNotesTemplateId: string | undefined;
    annualReportAccordions: IAnnualReportAccordionsData | undefined;
    flis: IFLIForNotes[] = [];
    annualReportDefaultSettings: IAnnualReportDefaultSettings = {} as IAnnualReportDefaultSettings;
    /** FX (REV-21512): null on non-FX reports — the notes components then behave exactly as today. */
    fxNotesContext: IFxNotesContext | null = null;
    /** FX (REV-21512): per-account display values for the FLI tables, keyed `id|accountName`. */
    fliFxStamps: Map<string, IFxNodeStamp> | null = null;
    currentAnnualReport: IAnnualReport | undefined;
    subscriptions: Subscription[] = [];
    isFliPrepared = false;
    autoFilledNotes: IAnnualReportAccordionsData;
    autofilledNotesFromChosenReport: IAnnualReportAccordionsData;
    previousYearNotes: IAnnualReportAccordionsData | undefined; // NOT USED FOR AUTOFILL. ONLY TO DISPLAY PREVIOUS YEAR DATA FOR SOME COMPONENTS.
    readonly balanceTemplate = 0;
    readonly incomeTemplate = 1;
    currentLanguage = this.getCurrentLanguageWithFallBack();
    // progress indicator
    progress = 70;
    completed = false;
    isVersionMismatch: boolean;
    readonly defaultLanguage = environment.defaultLanguage;

    private noPreviousDatePlaceholder = '-';

    private isSaveRequestInProgress = false;

    private notesAttachmentData: INotes | undefined;
    private notesAttachmentDefaultData: INotes | undefined;

    dataState: DataState = DataState.Loading;

    isPartOfArNavigation$: Observable<boolean>;
    reportLocale: string;

    highlightTodos = true;
    private userPreferences: IUserPreferences | undefined;
    currentFinStatements: any;

    public projectsList$: Observable<ProjectToCopyFrom[]>;

    private isCopyNotesStructureFromAnotherAnnualReportClicked = false;
    private isCopyNotesStructureFromAnotherAnnualReportClickedWithData = false;
    public isNotesStructureToCopyFromAnotherAnnualReportNull = false;

    public supportOperation = false;

    public balanceMappingTemplate: IAccountMappingTemplate | undefined;
    public incomeMappingTemplate: IAccountMappingTemplate | undefined;
    // Raw (pre-G&M) versions are forwarded to the SuperTable selection tree so users
    // pick from the original mapping structure, not the grouped/masked one (REV-21023).
    public rawBalanceMappingTemplate: IAccountMappingTemplate | undefined;
    public rawIncomeMappingTemplate: IAccountMappingTemplate | undefined;
    dataWasReset: boolean;

    private selectedEncryptedIdForCopyingNotesStructure: string | null = null;

    constructor(
        private annualReportProvider: AnnualReportProvider,
        private flisMapper: FlisMapper,
        private dateLocalPipe: DateLocalePipe,
        injector: Injector,
        dialog: MatDialog,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
        private readonly appStateDispatcher: AppStateDispatcher,
        protected readonly i18next: I18NextService,
        private readonly organizationProvider: OrganizationProvider,
        private readonly notesHelper: NotesHelper,
        private store: Store<ProjectsOverviewState>,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.NOTES;

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });


        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.NOTES);

        this.appStateDispatcher
            .getSupportOperation()
            .pipe(untilDestroyed(this))
            .subscribe((supportOperation) => {
                this.supportOperation = supportOperation;
            });

    }

    fireResetConfirmationMessage(): void {
        const notificationData: INotificationData = {
            descriptionKey: 'aumico_notes_update_confirmation_success',
        };
        this.fireSuccessNotification(notificationData);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.NOTES_TIME_SPENT_TILL_COMPLETION);

        this.userPreferences = this.localStorageService.getUserPrefrences();

        this.highlightTodos =
            this.userPreferences?.highlightTodosInNotes != null ? this.userPreferences?.highlightTodosInNotes : true;

        debug('ngOnInit');
        super.ngOnInit();

        this.dataState = DataState.Loading;

        this.annualReportProvider
            // REV-20899 save-side isolation: this whole-report object is persisted
            // verbatim on save, so it MUST be the RAW report. The bridged
            // finStatements would zero a v1 report's inherited legacy reclasses.
            // The DISPLAY finStatements (withLatestFrom below) stay reclass-aware,
            // so notes values still include reclassifications.
            .getActiveAnnualReport$()
            .pipe(
                withLatestFrom(
                    this.annualReportProvider.getActiveFinStatementsWithReclasses$(),
                    this.organizationProvider.getCurrentOrganization$(),
                ),
                map(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => {
                        if (
                            activeAnnualReport != null &&
                            activeAnnualReport.generalInformation != null &&
                            finStatements.length >= templateConstants.financialStatementMinimumLength &&
                            finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template !=
                            null &&
                            finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                        ) {
                            return [activeAnnualReport, finStatements, organization];
                        } else {
                            // Return null, so that we we this state can be handeled
                            return [null, finStatements, organization];
                        }
                    },
                ),
                tap(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => this.handleActiveAnnualReport(activeAnnualReport, finStatements, organization),
                ),
                filter(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => activeAnnualReport?.notes === null,
                ),
                mergeMap(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => of(activeAnnualReport),
                ),

                mergeMap((activeAnnualReport) => {
                    const autofillOptionNotes = activeAnnualReport.templateReportAutofillOptions?.[NOTES];
                    if (autofillOptionNotes && this.currentAnnualReport?.notes == null) {
                        this.annualReportProvider.dispatchGetAnnualReportForMappingAutoFill(autofillOptionNotes);
                        return this.annualReportProvider.getAnnualReportForUploadAndMappingAutoFill$();
                    }

                    return of(null);
                }),
                tap((annualReport) => {
                    if (annualReport?.notes != null && this.currentAnnualReport?.notes == null) {
                        this.isNotesStructureToCopyFromAnotherAnnualReportNull = annualReport.notes == null;

                        const totalText = this.i18next.t('aumico_notes_table_with_form_total', {
                            lng: this.reportLocale,
                        }) as string;

                        const accordions = this.notesHelper.copyNotesStructureFromAnotherAnnualReportIncludingData(
                            annualReport,
                            totalText,
                            this.balanceMappingTemplate,
                            this.incomeMappingTemplate,
                            this.reportLocale,
                            true,
                        );

                        this.annualReportAccordions = {
                            accordions: cloneDeep(accordions),
                        };

                        this.autofilledNotesFromChosenReport = {
                            accordions: cloneDeep(accordions),
                        };
                    } else {
                        this.autofilledNotesFromChosenReport = null;

                    }
                }),
                filter((autoFilledAnnualReport: IAnnualReport) => autoFilledAnnualReport?.notes == null),
                mergeMap(() => this.annualReportProvider.getAnnualReportForBalanceAutoFill$()),
                filter((autoFilledAnnualReport: IAnnualReport) => autoFilledAnnualReport != null),
                // 👇 Only emit when the value is different from the previous one
                distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
                untilDestroyed(this),
            )
            .subscribe((autoFilledAnnualReport: IAnnualReport) => {
                this.handleAutoFilledAnnualReport(autoFilledAnnualReport);
            });

        this.annualReportProvider
            .getAnnualReportForBalanceAutoFill$()
            .pipe(untilDestroyed(this))
            .subscribe((previousAnnualReport: IAnnualReport) => {
                if (previousAnnualReport != null) {
                    // Not USED FOR AUTOFILL. ONLY TO DISPLAY PREVIOUS YEAR DATA FOR SOME COMPONENTS
                    this.previousYearNotes = cloneDeep<IAnnualReportAccordionsData>(previousAnnualReport.notes);

                    const currentBalanceDate =
                        previousAnnualReport.generalInformation.balanceSheetDate;
                    const previousBalanceDate = previousAnnualReport.generalInformation.noPreviousData
                        ? '-'
                        : previousAnnualReport.generalInformation.balancePreviousYearDate;

                    const currentIncomeDate =
                        this.dateLocalPipe.transform(previousAnnualReport.generalInformation.incomeCurrentYearFromDate).toString() +
                        '-' +
                        (previousAnnualReport.generalInformation.incomeCurrentYearToDate != null
                            ? this.dateLocalPipe.transform(previousAnnualReport.generalInformation.incomeCurrentYearToDate).toString()
                            : '');

                    const previousIncomeDate = previousAnnualReport.generalInformation.noPreviousData
                        ? this.noPreviousDatePlaceholder
                        : this.dateLocalPipe.transform(previousAnnualReport.generalInformation.incomePreviousYearFromDate).toString() +
                        '-' +
                        (previousAnnualReport.generalInformation.incomePreviousYearToDate != null
                            ? this.dateLocalPipe.transform(previousAnnualReport.generalInformation.incomePreviousYearToDate).toString()
                            : '');

                    this.annualReportDefaultSettings = {
                        ...this.annualReportDefaultSettings,
                        annualReportSelectedForAutofillCurrentBalanceDate: currentBalanceDate,
                        annualReportSelectedForAutofillPreviousBalanceDate: previousBalanceDate,
                        annualReportSelectedForAutofillCurrentIncomeDate: currentIncomeDate,
                        annualReportSelectedForAutofillPreviousIncomeDate: previousIncomeDate,
                    };
                }
            });

        const projectsObservable$ = combineLatest([
            this.actionsListener$.pipe(ofType(ProjectsOverviewActions.projectsListFetched)),
            this.store.select(selectActiveProjectId)
        ]).pipe(
            map(([payload, activeProjectId]) => {
                return payload.response.projectsMetaInformation
                    .filter((project) => project != null && project.companyName != null)
                    .filter((project) => project != null && project.accountingBasis != null)
                    .filter((project) => project.projectId !== activeProjectId);
            }),
            untilDestroyed(this)
        );

        const annualReportsMetaObservable$ = this.annualReportProvider.getAnnualReportsListToCopyFrom$().pipe(
            filter((annualReportsList: AnnualReportMetaInformationDTO[]) => {
                return annualReportsList != null;
            }),
            map((annualReportsList) => {
                const filtered = annualReportsList
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.companyName != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.legalForm != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.accountingBasis != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.locale != null)
                    .filter(
                        (annualMetaInfo) =>
                            annualMetaInfo.locale.toLocaleLowerCase() === this.reportLocale?.toLocaleLowerCase(),
                    )

                return filtered;
            }),
            untilDestroyed(this)
        );

        // Combine both observables
        this.projectsList$ = combineLatest([projectsObservable$, annualReportsMetaObservable$]).pipe(
            map(([projects, annualReportsMeta]) => {

                return projects
                    .map((project): ProjectToCopyFrom | null => {

                        // Find matching annual report meta by annual report meta id
                        const matchingAnnualReport = annualReportsMeta.find(
                            (metaInfo) =>
                                metaInfo._id === project.annualReportMetaData
                        );

                        if (!matchingAnnualReport) {
                            return null; // Remove project from projects
                        }
                        return {
                            ...project,
                            annualReportEncryptedId: matchingAnnualReport.annualReportEncryptedId,
                            legalForm: matchingAnnualReport.legalForm,
                        };

                    })
                    .filter((project): project is ProjectToCopyFrom => project !== null);
            }),
            untilDestroyed(this)
        );

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        this.actionsListener$
            .pipe(ofType(onGetAnnualReportToCopyNotesStructureFromSuccessfully), untilDestroyed(this))
            .subscribe(({ payload: annualReportToCopyNotesStructureFrom }) => {
                if (this.isCopyNotesStructureFromAnotherAnnualReportClicked) {
                    if (this.isCopyNotesStructureFromAnotherAnnualReportClickedWithData) {
                        if (!this.currentAnnualReport.templateReportAutofillOptions) {
                            this.currentAnnualReport.templateReportAutofillOptions = {} as Record<AutofillOptions, string>;
                        }

                        this.currentAnnualReport.templateReportAutofillOptions[NOTES] = this.selectedEncryptedIdForCopyingNotesStructure;

                        const totalText = this.i18next.t('aumico_notes_table_with_form_total', {
                            lng: this.reportLocale,
                        }) as string;

                        const accordions = this.notesHelper.copyNotesStructureFromAnotherAnnualReportIncludingData(
                            annualReportToCopyNotesStructureFrom,
                            totalText,
                            this.balanceMappingTemplate,
                            this.incomeMappingTemplate,
                            this.reportLocale,
                            true,
                        );

                        this.annualReportAccordions = {
                            accordions: cloneDeep(accordions),
                        };
                    } else {
                        const accordions = this.notesHelper.copyNotesStructureFromAnotherAnnualReport(
                            annualReportToCopyNotesStructureFrom,
                            this.reportLocale,
                        );

                        this.annualReportAccordions = {
                            accordions: cloneDeep(accordions),
                        };
                    }
                }
            });

        this.actionsListener$
            .pipe(ofType(onGetAnnualReportToCopyNotesStructureFromFailed), untilDestroyed(this))
            .subscribe(() => {
                if (this.isCopyNotesStructureFromAnotherAnnualReportClicked) {
                    this.selectedEncryptedIdForCopyingNotesStructure = null;
                    this.fireErrorMessage('aumico_notes_copy_notes_structure_fetch_error');
                }
            });
    }

    handleAutoFilledAnnualReport(annualReport: IAnnualReport) {
        if (annualReport.notes == null || annualReport.notes.accordions == null || annualReport.notes.accordions.length === 0) {
            return
        }
        this.autoFilledNotes = annualReport.notes as IAnnualReportAccordionsData;
    }

    private handleActiveAnnualReport(
        annualReport: IAnnualReport | undefined | null,
        finStatements: IFinStatements[],
        organization: GetOrganizationDto | undefined | null,
    ): void {
        if (annualReport == null) {
            this.dataState = DataState.Error;
            return;
        }

        debug('handleActiveAnnualReport');
        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;

        this.currentAnnualReport = cloneDeep<IAnnualReport>(annualReport);
        this.currentFinStatements = cloneDeep<IFinStatements>(finStatements);
        this.reportLocale = annualReport.locale.toLowerCase();

        this.prepareFlisWithAccounts(this.currentAnnualReport, this.currentFinStatements);
        this.handleSettingsAndCombination(this.currentAnnualReport);
        const isNotesOfAnnualReportEmpty = this.currentAnnualReport.notes
            ? Object.keys(this.currentAnnualReport.notes).length === 0
            : true;

        if (isNotesOfAnnualReportEmpty) {
            this.isNotesSavedBefore = false;
        } else {
            this.isNotesSavedBefore = true;
            this.annualReportAccordions = cloneDeep(this.currentAnnualReport.notes) as IAnnualReportAccordionsData;
        }

        if (annualReport.notes != null) {
            this.checkVersionMismatch(
                [FCPState.PAID, FCPState.TRIAL].includes(organization?.fcpActivationDetails?.subscriptionState) &&
                annualReport?.fcp != null,
                annualReport,
            );
        } else {
            this.isVersionMismatch = false;
        }
    }

    private handleSettingsAndCombination(annualReport: IAnnualReport): void {
        if (annualReport.generalInformation == null) {
            this.dataState = DataState.Error;

            return;
        }
        this.annualReportDefaultSettings.currency = annualReport.generalInformation.currency
            ? annualReport.generalInformation.currency.name
            : '';
        this.annualReportDefaultSettings.displayCurrency =
            annualReport.setupData?.fxEnabled === true
                ? annualReport.generalInformation.displayCurrency?.name ?? undefined
                : undefined;
        this.annualReportDefaultSettings.currentBalanceDate = annualReport.generalInformation.balanceSheetDate;
        this.annualReportDefaultSettings.previousBalanceDate = annualReport.generalInformation.noPreviousData
            ? '-'
            : annualReport.generalInformation.balancePreviousYearDate;
        this.annualReportDefaultSettings.currentIncomeDate =
            this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearFromDate).toString() +
            '-' +
            (annualReport.generalInformation.incomeCurrentYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearToDate).toString()
                : '');
        this.annualReportDefaultSettings.previousIncomeDate = annualReport.generalInformation.noPreviousData
            ? this.noPreviousDatePlaceholder
            : this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearFromDate).toString() +
            '-' +
            (annualReport.generalInformation.incomePreviousYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearToDate).toString()
                : '');
        this.combination = {
            legalForm: annualReport.generalInformation.company
                ? annualReport.generalInformation.company.legalform._id
                : '',
            accountBasis: annualReport.generalInformation.accountingBasis
                ? annualReport.generalInformation.accountingBasis._id
                : '',
        };

        if (annualReport.annualReportTemplate != null && annualReport.annualReportTemplate.notesTemplateId != null) {
            this.arTemplateNotesTemplateId = annualReport.annualReportTemplate.notesTemplateId;
        }

        this.checkDataState();
    }

    prepareFlisWithAccounts(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        // REV-22548: the same recipe Abschluss & Druck uses to decide whether the notes are out of date.
        const templates = NotesLiveDataHelper.buildTemplates(annualReport, finStatements);
        this.fxNotesContext = null;
        this.fliFxStamps = null;
        if (templates == null) {
            // No mapped statements yet: the notes open without mapping-derived rows instead of crashing.
            this.flis = [];
            this.isFliPrepared = true;
            this.checkDataState();
            return;
        }

        this.balanceMappingTemplate = templates.balance;
        this.incomeMappingTemplate = templates.income;

        // Keep the untransformed templates so the SuperTable can use the raw Mapping
        // structure for account selection (REV-21023). FLIs and other notes components
        // continue to use the transformed templates above.
        this.rawBalanceMappingTemplate = templates.rawBalance;
        this.rawIncomeMappingTemplate = templates.rawIncome;

        // FX (REV-21512): decorate the 03b view models once and stamp `__fx` onto BOTH template
        // shapes — the closingReClass pattern: the app calculates, the lib only reads. Values are
        // re-derived on every page load / reset, never persisted (the stamped statements are the
        // page-local bridged clone, not the save payload). Abschluss & Druck runs the same step.
        const fx = NotesLiveDataHelper.applyFx(
            annualReport,
            finStatements,
            templates,
            this.i18next.t('aumico_annual_report_fx_notes_reserve_label') as string,
        );
        if (fx != null) {
            this.fxNotesContext = fx.context;
            this.fliFxStamps = fx.fliFxStamps;
        }

        // Pass TRANSFORMED templates to flisMapper
        // Now FlisMapper will extract FLIs that reflect grouped/split/renamed/sorted accounts
        this.flis = this.flisMapper.getFlisOfBalanceAndIncomeTemplates(
            this.balanceMappingTemplate,
            this.incomeMappingTemplate,
            this.fliFxStamps ?? undefined,
        );

        this.isFliPrepared = true;

        this.checkDataState();
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.NOTES_TIME_SPENT_TILL_COMPLETION);
        this.isSaveRequestInProgress = true;

        const notesToBeSaved = {
            ...this.notesAttachmentData,
            version: this.currentAnnualReport.notes?.version,
        };
        this.currentAnnualReport.notes = notesToBeSaved;

        // REV-22548: whatever the notes page saves was refreshed against the current statements when
        // it loaded, so the saved notes are as current as those statements — record their version
        // even when the refresh found nothing to change, or the «Änderungen erkannt» banner never clears.
        this.currentAnnualReport.notes.version = this.currentFinStatements[templateConstants.balanceTemplate].version;

        if (this.isNotesStructureToCopyFromAnotherAnnualReportNull) {
            this.currentAnnualReport.notes = null;
        }

        this.dispatchSaveAnnualReport(this.currentAnnualReport);
        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            this.notesAttachmentDefaultData = cloneDeep<IAnnualReportAccordionsData>(this.notesAttachmentData);
            this.savedSuccessfully.next(true);
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    private checkDataState(): void {
        if (this.combination != null && this.annualReportDefaultSettings != null && this.isFliPrepared) {
            this.dataState = DataState.Success;
        }
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.currentAnnualReport && this.currentAnnualReport.notes == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            const isNotesEqualDefaultValue = fastDeepEqual(
                JSON.stringify(this.notesAttachmentData),
                JSON.stringify(this.notesAttachmentDefaultData),
            );

            if (isNotesEqualDefaultValue) {
                return of(false);
            } else {
                // REV-22548: compared by what the user can change, not as raw text — the editor's copy
                // differs from the saved notes in view markers, settings copied in on open and the version.
                return of(
                    NotesUnsavedChangesHelper.isChanged(
                        this.currentAnnualReport.notes as IAnnualReportAccordionsData,
                        this.notesAttachmentData,
                    ),
                );
            }
        }
    }

    outputtedAccordionsData(accordionsData: IAnnualReportAccordionsData, dataWasReset: boolean) {
        debug('outputtedAccordionsData', accordionsData, dataWasReset);
        if (dataWasReset) {
            // Set it to true if once reset, but don't reset it again
            this.dataWasReset = true;
        }
        this.notesAttachmentData = cloneDeep<IAnnualReportAccordionsData>(accordionsData);
    }

    outputtedAccordionsDefaultData(accordionsData: IAnnualReportAccordionsData) {
        debug(accordionsData, 'outputtedAccordionsDefaultData');

        this.notesAttachmentDefaultData = cloneDeep<IAnnualReportAccordionsData>(accordionsData);
    }

    // progress indicator
    adjustProgressPercentage() {
        // progress of circular progress indicator should be changed here
        debug('progressPercentage');
        debug(this.progress);
    }

    adjustCompletionState() {
        // status of progress indicator should be changed here
        debug('isCompleted');

        debug(this.completed);
    }

    public onHighlightTodosChanged(highlightTodos: boolean): void {
        this.highlightTodos = highlightTodos;

        this.authProvider.dispatchUpdateUserPreferences({
            ...this.userPreferences,
            highlightTodosInNotes: this.highlightTodos,
        });
        this.localStorageService.setUserPrefrences({
            ...this.userPreferences,
            highlightTodosInNotes: this.highlightTodos,
        });
    }

    public onSelectedAnnualReportIdToCopyNotesStructureFrom(selectedAnnualReportId: string): void {
        const dialogRef = this.dialog.open<ModalComponent, ModalData, OkCancelDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                footerButtons: [
                    {
                        localizationKey:
                            'aumico_notes_copy_notes_structure_from_another_annual_report_confirmation_dialog_yes_button',
                        action: OkCancelDialogActions.OK,
                        buttonColor: 'primary',
                    },
                    {
                        localizationKey:
                            'aumico_notes_copy_notes_structure_from_another_annual_report_confirmation_dialog_yes_inclusive_data_button',
                        action: OkCancelDialogActions.ALERTNATIVE_OK,
                        buttonColor: 'basic',
                    },
                    {
                        localizationKey:
                            'aumico_notes_copy_notes_structure_from_another_annual_report_confirmation_dialog_cancel_button',
                        action: OkCancelDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                content: {
                    text: [
                        'aumico_notes_copy_notes_structure_from_another_annual_report_confirmation_dialog_description',
                    ],
                    icon: 'document-information',
                },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result === OkCancelDialogActions.CANCEL) {
                    return;
                }

                if (result === OkCancelDialogActions.OK) {
                    this.isCopyNotesStructureFromAnotherAnnualReportClicked = true;
                    this.isCopyNotesStructureFromAnotherAnnualReportClickedWithData = false;
                    this.annualReportProvider.dispatchGetAnnualReportToTakeNotesStructureFrom(selectedAnnualReportId);
                }
                if (result === OkCancelDialogActions.ALERTNATIVE_OK) {
                    this.isCopyNotesStructureFromAnotherAnnualReportClicked = true;
                    this.isCopyNotesStructureFromAnotherAnnualReportClickedWithData = true;
                    this.annualReportProvider.dispatchGetAnnualReportToTakeNotesStructureFrom(selectedAnnualReportId);
                }

                this.selectedEncryptedIdForCopyingNotesStructure = selectedAnnualReportId;


            });
    }

    private checkVersionMismatch(isFcpActive: boolean, annualReport: IAnnualReport): void {
        // REV-22548: the same rule Abschluss & Druck warns from.
        this.isVersionMismatch = NotesVersionHelper.isNotesOutOfDate(annualReport, isFcpActive);
    }

    onCopyNotesStructureFromAnotherAnnualReportButtonClicked(): void {
        this.store.dispatch(ProjectsOverviewActions.fetchProjectsList({ filter: { reportTypeIds: [ReportType.ANNUAL] } }));

        this.annualReportProvider.dispatchAnnualReportsListToCopyFrom();

    }
}
